import { createFileRoute } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"
import { toast } from "sonner"
import { SendInviteForm } from "@/components/forms/send-invite-form"
import { Page } from "@/components/page"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"
import type { Invitation } from "../../../../../../api/services/invitation/types"

export const Route = createFileRoute("/_authed/_professional/professional/invitations")({
  component: InvitationsPage,
})

function InvitationsPage() {
  const utils = trpc.useUtils()

  const { data: invitations = [], isLoading } = trpc.invitations.list.useQuery({})

  const revokeInvitation = trpc.invitations.revoke.useMutation({
    onSuccess: () => {
      toast.success("Invitation revoked successfully!")
      utils.invitations.list.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleRevoke = (invitationId: string) => {
    revokeInvitation.mutate({ invitationId })
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "PENDING":
        return "secondary"
      case "ACCEPTED":
        return "default"
      case "DECLINED":
        return "destructive"
      case "EXPIRED":
        return "outline"
      default:
        return "secondary"
    }
  }

  const columns: ColumnDef<Invitation>[] = [
    {
      accessorKey: "inviteeEmail",
      header: "Email",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string
        return <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
      },
    },
    {
      accessorKey: "role",
      header: "Role",
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as Date
        return date.toLocaleDateString()
      },
    },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      cell: ({ row }) => {
        const date = row.getValue("expiresAt") as Date
        return date.toLocaleDateString()
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const invitation = row.original
        const canRevoke = invitation.status === "PENDING"

        return (
          <Button
            variant="destructive"
            size="sm"
            disabled={!canRevoke || revokeInvitation.isPending}
            onClick={() => handleRevoke(invitation.id)}
          >
            {revokeInvitation.isPending ? "Revoking..." : "Revoke"}
          </Button>
        )
      },
    },
  ]

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Manage Invitations
        </Typography>
      }
    >
      <div className="space-y-8">
        {/* Send Invitation Form */}
        <SendInviteForm />

        {/* Invitations Table */}
        <div className="rounded-lg border p-6">
          <Typography as="h2" size="lg" className="mb-4">
            Sent Invitations
          </Typography>

          {isLoading ? (
            <div>Loading invitations...</div>
          ) : (
            <DataTable columns={columns} data={invitations} />
          )}
        </div>
      </div>
    </Page>
  )
}
